export const BUY_BOX_DEAL_STRUCTURES = [
  "SBA",
  "SELLER_FINANCING",
  "CONVENTIONAL",
  "ALL_CASH",
] as const;

export type BuyBoxDealStructure = (typeof BUY_BOX_DEAL_STRUCTURES)[number];
export type BuyBoxCurrency = "USD" | "CAD" | "NZD";

export interface BuyBoxLocation {
  subdivisionId: number | null;
  countryCode: string;
  label: string;
}

export interface BuyBoxRange {
  min: number | null;
  max: number | null;
}

export interface BuyBoxCriteria {
  businessTypeIds: number[];
  locations: BuyBoxLocation[];
  currency: BuyBoxCurrency;
  askingPrice: BuyBoxRange;
  revenue: BuyBoxRange;
  sde: BuyBoxRange;
  ebitda: BuyBoxRange;
  dealStructures: BuyBoxDealStructure[];
  /** Free text from the earlier thesis form that has no structured match. */
  unmapped: {
    industries: string[];
    geography: string[];
    dealStructures: string[];
  };
}

const DEAL_STRUCTURE_ALIASES: Record<BuyBoxDealStructure, RegExp> = {
  SBA: /\bsba\b|7\s*\(?a\)?/i,
  SELLER_FINANCING: /seller/i,
  CONVENTIONAL: /conventional|bank|senior debt/i,
  ALL_CASH: /all[\s-]*cash|cash only|no debt/i,
};

const CURRENCIES: BuyBoxCurrency[] = ["USD", "CAD", "NZD"];

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function strings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter(Boolean);
}

function buyBoxLocation(value: unknown): BuyBoxLocation | null {
  const raw = record(value);
  const countryCode =
    typeof raw.countryCode === "string" ? raw.countryCode.trim() : "";
  const label = typeof raw.label === "string" ? raw.label.trim() : "";
  const subdivisionId =
    Number.isInteger(raw.subdivisionId) && (raw.subdivisionId as number) > 0
      ? (raw.subdivisionId as number)
      : null;
  if (countryCode.length !== 2 || !label) return null;
  if (raw.subdivisionId != null && subdivisionId == null) return null;
  return { subdivisionId, countryCode: countryCode.toUpperCase(), label };
}

function amount(value: unknown): number | null {
  const parsed = typeof value === "string" ? Number(value) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) && parsed >= 0
    ? parsed
    : null;
}

/** Reads `minX`/`maxX`, falling back to the older `xMin`/`xMax` seed keys. */
function range(raw: Record<string, unknown>, key: string): BuyBoxRange {
  const suffix = key.charAt(0).toUpperCase() + key.slice(1);
  let min = amount(raw[`min${suffix}`] ?? raw[`${key}Min`]);
  let max = amount(raw[`max${suffix}`] ?? raw[`${key}Max`]);
  if (min != null && max != null && min > max) [min, max] = [max, min];
  return { min, max };
}

function dealStructures(values: string[]) {
  const mapped = new Set<BuyBoxDealStructure>();
  const unmapped: string[] = [];
  for (const value of values) {
    const key = BUY_BOX_DEAL_STRUCTURES.find(
      (structure) =>
        structure === value.toUpperCase() ||
        DEAL_STRUCTURE_ALIASES[structure].test(value),
    );
    if (key) mapped.add(key);
    else unmapped.push(value);
  }
  return { mapped: [...mapped], unmapped };
}

/**
 * Normalizes any stored thesis criteria JSON into a buy box. Tolerates the
 * original free-text form, partial objects, and older seed key names, so a
 * bad or legacy row never breaks matching.
 */
export function parseBuyBoxCriteria(json: unknown): BuyBoxCriteria {
  const raw = record(json);
  const businessTypeIds = Array.isArray(raw.businessTypeIds)
    ? [
        ...new Set(
          raw.businessTypeIds.filter(
            (id): id is number => Number.isInteger(id) && (id as number) > 0,
          ),
        ),
      ]
    : [];
  const locations = Array.isArray(raw.locations)
    ? raw.locations.flatMap((location) => {
        const parsed = buyBoxLocation(location);
        return parsed ? [parsed] : [];
      })
    : [];
  const currency = CURRENCIES.find((code) => code === raw.currency) ?? "USD";
  const structures = dealStructures(strings(raw.dealStructures));

  return {
    businessTypeIds,
    locations,
    currency,
    askingPrice: range(raw, "askingPrice"),
    revenue: range(raw, "revenue"),
    sde: range(raw, "sde"),
    ebitda: range(raw, "ebitda"),
    dealStructures: structures.mapped,
    unmapped: {
      industries: strings(raw.industries),
      geography: strings(raw.geography ?? raw.geographies),
      dealStructures: structures.unmapped,
    },
  };
}

/** A buy box can be matched once it names an industry or a location. */
export function isBuyBoxMatchable(criteria: BuyBoxCriteria): boolean {
  return criteria.businessTypeIds.length > 0 || criteria.locations.length > 0;
}
